import React, { useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Typography,
	CircularProgress,
	Alert,
	Chip,
	Divider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { analyzeTestVsProd, AnalyzeResponse } from '../../../Services/dataComparison';

interface TestAnalysisModalProps {
	open: boolean;
	handleClose: () => void;
	processId: string;
}

export const TestAnalysisModal: React.FC<TestAnalysisModalProps> = ({
	open,
	handleClose,
	processId,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<AnalyzeResponse | null>(null);
	const [objective, setObjective] = useState('');

	const handleAnalyze = async () => {
		setLoading(true);
		setError(null);
		setResult(null);

		try {
			const response = await analyzeTestVsProd({
				processId,
				objective: objective || undefined,
			});
			setResult(response);
		} catch (e: any) {
			console.error('Analysis error:', e);
			setError(e.message || "Erreur lors de l'analyse");
		} finally {
			setLoading(false);
		}
	};

	const handleCloseModal = () => {
		setResult(null);
		setError(null);
		setObjective('');
		handleClose();
	};

	const handleNewAnalysis = () => {
		setResult(null);
		setError(null);
	};

	const formatDuration = (nanoseconds: number): string => {
		const seconds = nanoseconds / 1_000_000_000;
		if (seconds < 60) {
			return `${seconds.toFixed(1)}s`;
		}
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
	};

	return (
		<Dialog
			open={open}
			onClose={handleCloseModal}
			maxWidth="lg"
			fullWidth
			PaperProps={{ sx: { minHeight: '60vh' } }}
		>
			<DialogTitle>Analyse Test vs Production</DialogTitle>

			<DialogContent>
				{!result && !loading && (
					<Box sx={{ mt: 1 }}>
						<Alert severity="info" sx={{ mb: 2 }}>
							Cette analyse compare les données du dataset de test avec la
							production et génère un rapport via IA.
						</Alert>

						<TextField
							label="Objectif de l'analyse (optionnel)"
							value={objective}
							onChange={(e) => setObjective(e.target.value)}
							placeholder="Ex: Vérifier que les métriques de conversion sont cohérentes"
							fullWidth
							multiline
							rows={2}
							size="small"
						/>
					</Box>
				)}

				{loading && (
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						py={8}
					>
						<CircularProgress size={60} />
						<Typography variant="body1" sx={{ mt: 2 }}>
							Analyse en cours...
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Cela peut prendre quelques minutes
						</Typography>
					</Box>
				)}

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{result && (
					<Box>
						{/* Metadata chips */}
						<Box display="flex" flexWrap="wrap" gap={1} mb={2}>
							<Chip
								label={`Modèle: ${result.metadata.model}`}
								size="small"
								variant="outlined"
							/>
							<Chip
								label={`Tokens: ${result.metadata.tokensUsed.toLocaleString()}`}
								size="small"
								variant="outlined"
							/>
							<Chip
								label={`Durée: ${formatDuration(result.metadata.duration)}`}
								size="small"
								variant="outlined"
							/>
							<Chip
								label={`Tables: ${result.metadata.tablesCompared.length}`}
								size="small"
								variant="outlined"
							/>
						</Box>

						{/* Stats comparison */}
						<Box
							display="flex"
							gap={2}
							mb={2}
							sx={{
								'& > div': {
									flex: 1,
									p: 1.5,
									borderRadius: 1,
									bgcolor: 'grey.100',
								},
							}}
						>
							<Box>
								<Typography variant="subtitle2" gutterBottom>
									Test ({result.metadata.testDataset})
								</Typography>
								<Typography variant="body2">
									{result.metadata.testStats.totalRows.toLocaleString()} lignes
								</Typography>
								<Typography variant="body2">
									{result.metadata.testStats.tableCount} tables,{' '}
									{result.metadata.testStats.columnCount} colonnes
								</Typography>
							</Box>
							<Box>
								<Typography variant="subtitle2" gutterBottom>
									Production ({result.metadata.prodDataset})
								</Typography>
								<Typography variant="body2">
									{result.metadata.prodStats.totalRows.toLocaleString()} lignes
								</Typography>
								<Typography variant="body2">
									{result.metadata.prodStats.tableCount} tables,{' '}
									{result.metadata.prodStats.columnCount} colonnes
								</Typography>
							</Box>
						</Box>

						<Divider sx={{ my: 2 }} />

						{/* Analysis report */}
						<Box
							component="pre"
							sx={{
								whiteSpace: 'pre-wrap',
								wordBreak: 'break-word',
								fontFamily: 'inherit',
								fontSize: '0.875rem',
								lineHeight: 1.6,
								m: 0,
								p: 2,
								bgcolor: 'grey.50',
								borderRadius: 1,
								maxHeight: '50vh',
								overflow: 'auto',
							}}
						>
							{result.analysis}
						</Box>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				{result ? (
					<>
						<Button onClick={handleNewAnalysis} disabled={loading}>
							Nouvelle analyse
						</Button>
						<Button onClick={handleCloseModal} disabled={loading}>
							Fermer
						</Button>
					</>
				) : (
					<>
						<Button onClick={handleCloseModal} disabled={loading}>
							Annuler
						</Button>
						<Button
							onClick={handleAnalyze}
							variant="contained"
							disabled={loading}
							startIcon={
								loading ? <CircularProgress size={16} /> : <PlayArrowIcon />
							}
						>
							Lancer l'analyse
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};
